import { useCallback } from "react";
import { deleteDoc, doc, getDoc, setDoc } from "firebase/firestore";

import { FirestoreUtils } from "../../utils";

import { useCurrentUser } from "../../state/FirebaseAuthState";

import { AlertView, NotificationAlert } from "@msend/types";

/**
 * This hook returns a function that can be used
 * to create an alert view for the current user.
 */
export const useCreateAlertView = () => {
  const user = useCurrentUser();
  return useCallback(
    async (id: string) => {
      if (!user) return;
      const ref = FirestoreUtils.createDocRef<AlertView>(
        `users/${user.uid}/alerts/${id}`
      );
      // Only create the alert if it does not exist
      const snapshot = await getDoc(ref);
      if (!snapshot.exists()) {
        await setDoc(ref, {
          id,
          created: FirestoreUtils.timestamp(),
        });
      }
    },
    [user]
  );
};

/**
 * This hook returns a function that can be used to
 * acknowledge an alert for the current user..
 */
export const useAcknowledgeAlert = () => {
  const user = useCurrentUser();
  return useCallback(
    async (id: string, response?: string) => {
      if (!user) return;
      const ref = FirestoreUtils.createDocRef<AlertView>(
        `users/${user.uid}/alerts/${id}`
      );
      await setDoc(
        ref,
        {
          acknowledged: FirestoreUtils.timestamp(),
          ...(response ? { response } : null),
        },
        { merge: true }
      );
    },
    [user]
  );
};

/**
 * This hook returns a function that can be used to create an alert.
 */
export const useCreateAlert = () => {
  const user = useCurrentUser();
  return useCallback(
    async (
      alertOptions: Pick<
        NotificationAlert,
        "message" | "title" | "priority" | "acknowledgeText" | "variant"
      > & { expiry: Date | null }
    ) => {
      if (!user) return;
      const { expiry, ...options } = alertOptions;
      const ref = doc(FirestoreUtils.createColRef<NotificationAlert>("alerts"));
      await setDoc(ref, {
        id: ref.id,
        created: FirestoreUtils.timestamp(),
        createdBy: user.uid,
        type: "notification",
        expiry: expiry ? FirestoreUtils.timestamp(expiry) : null,
        ...options,
      });
    },
    [user]
  );
};

/**
 * This hook returns a function that can be used to delete an alert.
 */
export const useDeleteAlert = () => {
  return useCallback(async (alertId: string) => {
    const ref = FirestoreUtils.createDocRef<NotificationAlert>(`alerts/${alertId}`);
    await deleteDoc(ref);
  }, []);
};
